import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AuthorMirrorConstellationCard from './AuthorMirrorConstellationCard'
import AuthorMirrorAuthorDetailsCard from './AuthorMirrorAuthorDetailsCard'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectAnaliticDetailsStateAuthorDetails,
  selectAnalyticDetailsStateAnalyticDetails,
  selectAnalyticDetailsStateGetAuthorRecoV3Error,
  selectAnalyticDetailsStateGetAuthorRecoV3Success,
  selectAnalyticDetailsStateWorkMirrorBookDetailsCardValues,
} from '../../../../store/getters/analytics-details/analytic-details-v2.getters'
import {
  analyticDetailsV2StateGetAuthorDetailsData,
  analyticDetailsV2StateGetBookDetailsData,
} from '../../../../store/actions/analytics-details/analytic-details-v2.actions'
import { setShowSnackBar } from '../../../../store/reducers/web-app.reducer'
import AuthorMirrorListCard from './AuthorMirrorListCard'
import GlDateManager from '../../../share/GlDateManager'
import { AuthorMirrorAuthorInterface } from '../../../../core/interface/report/author-mirror-author.interface'
import WorkMirrorBookDetailsCard from '../work-mirror/WorkMirrorBookDetailsCard'
import { WorkMirrorBookInterface } from '../../../../core/interface/report/work-mirror-book.interface'

const AnalyticDetailsAuthorMirrorView = ({
  handleSaveAnalytics,
  handleShareAnalytics,
}: {
  handleSaveAnalytics: () => void
  handleShareAnalytics: () => void
}) => {
  const { t } = useTranslation()
  const [openListCard, setOpenListCard] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const getAuthorRecoV3Success = useAppSelector(
    selectAnalyticDetailsStateGetAuthorRecoV3Success,
  )
  const getAuthorRecoV3Error = useAppSelector(
    selectAnalyticDetailsStateGetAuthorRecoV3Error,
  )

  const analyticsDetails = useAppSelector(
    selectAnalyticDetailsStateAnalyticDetails,
  )

  const handleOpenListView = () => {
    setOpenListCard(!openListCard)
  }

  const {
    isLoadingBookDetailsGetBookReco,
    bookDetailsBookReco,
    isLoadingGetBookDetails,
    isLoadingGetEditions,
    isLoadingGetB2BBookDetails,
    bookDetailsSelected,
    bookDetailsSelectedEditions,
    bookDetailsSelectedB2BInfo,
  } = useAppSelector(selectAnalyticDetailsStateWorkMirrorBookDetailsCardValues)

  const { authorDetailsAuthorReco, authorDetailsSelected } = useAppSelector(
    selectAnaliticDetailsStateAuthorDetails,
  )

  const handleCloseList = () => {
    setOpenListCard(false)
  }

  useEffect(() => {
    if (getAuthorRecoV3Error) {
      dispatch(setShowSnackBar(t('snackbarMessage.errorMessage')))
    }
  }, [getAuthorRecoV3Success, getAuthorRecoV3Error])

  const [authorIdHovered, setAuthorIdHovered] = useState<string | null>(null)
  const handleAuthorHovered = useCallback(
    (authorId: string | null) => {
      setAuthorIdHovered(authorId)
    },
    [authorIdHovered],
  )

  const handleCloseAuthorDetails = useCallback(() => {
    void dispatch(
      analyticDetailsV2StateGetAuthorDetailsData({ authorDetails: null }),
    )
  }, [authorDetailsSelected])

  const handleCloseBookDetails = useCallback(() => {
    void dispatch(
      analyticDetailsV2StateGetBookDetailsData({ bookDetails: null }),
    )
  }, [bookDetailsSelected])

  const handleAuthorSelected = useCallback(
    (authorDetails: AuthorMirrorAuthorInterface | null) => {
      if (!authorDetails) {
        return
      }

      void dispatch(
        analyticDetailsV2StateGetAuthorDetailsData({ authorDetails }),
      )

      handleCloseBookDetails()
    },
    [authorDetailsSelected],
  )

  const handleBookSelected = useCallback(
    (bookDetails: WorkMirrorBookInterface | null) => {
      void dispatch(analyticDetailsV2StateGetBookDetailsData({ bookDetails }))
      handleCloseAuthorDetails()
    },
    [
      bookDetailsSelected,
      bookDetailsSelectedEditions,
      bookDetailsSelectedB2BInfo,
    ],
  )

  return (
    <div className="flex-auto flex flex-col p-8 gap-2">
      <GlDateManager
        prefix={t('ReportReaderShipProfileDate')}
        date={analyticsDetails.modified}
      />
      <div className="flex-auto flex flex-row gap-2">
        <AuthorMirrorConstellationCard
          openListCard={openListCard}
          handleOpenListView={handleOpenListView}
          handleSaveAnalytics={handleSaveAnalytics}
          handleShareAnalytics={handleShareAnalytics}
          handleAuthorHovered={handleAuthorHovered}
          authorDetailsSelected={authorDetailsSelected}
          handleAuthorSelected={handleAuthorSelected}
        />
        {authorDetailsSelected && (
          <AuthorMirrorAuthorDetailsCard
            handleAuthorSelected={handleAuthorSelected}
            handleBookSelected={handleBookSelected}
            authorDetailsAuthorReco={authorDetailsAuthorReco}
            openListCard={openListCard}
            authorDetailsSelected={authorDetailsSelected}
            handleCloseAuthorDetails={handleCloseAuthorDetails}
          />
        )}

        {bookDetailsSelected && (
          <WorkMirrorBookDetailsCard
            isLoadingBookDetailsGetBookReco={isLoadingBookDetailsGetBookReco}
            bookDetailsBookReco={bookDetailsBookReco}
            isLoadingGetBookDetails={isLoadingGetBookDetails}
            isLoadingGetEditions={isLoadingGetEditions}
            isLoadingGetB2BBookDetails={isLoadingGetB2BBookDetails}
            openListCard={openListCard}
            bookDetails={bookDetailsSelected}
            bookDetailsSelectedEditions={bookDetailsSelectedEditions}
            bookDetailsSelectedB2BInfo={bookDetailsSelectedB2BInfo}
            handleCloseBookDetails={handleCloseBookDetails}
          />
        )}

        {!bookDetailsSelected && !authorDetailsSelected && openListCard && (
          <AuthorMirrorListCard
            authorIdHovered={authorIdHovered}
            handleCloseList={handleCloseList}
          />
        )}
      </div>
    </div>
  )
}

export default AnalyticDetailsAuthorMirrorView
